import { useAuth } from './store';
import { debugAuthState, clearAuthData } from './debug';
import { authMonitor } from './monitoring';

/**
 * Emergency fix for stuck loading screen
 * Run this in console: window.fixAuthLoading()
 */
export async function fixAuthLoading() {
  console.group('🚨 Auth Loading Fix');
  
  try {
    // Step 1: Log current state
    console.log('1. Current auth state:');
    await debugAuthState();
    
    // Step 2: Force loading to false
    console.log('\n2. Forcing loading state to false...');
    const authStore = useAuth.getState();
    authStore.setLoading(false);
    
    // Step 3: Check if we have a session but no user
    const { session, user } = authStore;
    if (session && !user) {
      console.log('\n3. Found session without user profile, attempting to fetch...');
      
      // Try to fetch the user profile
      try {
        const { supabase } = await import('../supabase/client');
        const { data: profile, error } = await supabase
          .from('users')
          .select('*, creator_profile:creator_profiles(*)')
          .eq('id', session.user.id)
          .single();
          
        if (profile && !error) {
          console.log('✅ Profile fetched successfully');
          authStore.setUser({
            ...profile,
            status: profile.status || 'active',
            creator_profile: profile.creator_profile?.[0] || null
          });
        } else {
          console.error('❌ Profile fetch failed:', error);
        }
      } catch (error) {
        console.error('❌ Error fetching profile:', error);
      }
    }
    
    // Step 4: Log final state
    console.log('\n4. Final auth state:');
    const finalState = useAuth.getState();
    console.log({
      hasUser: !!finalState.user,
      hasSession: !!finalState.session,
      loading: finalState.loading,
      error: finalState.error
    });
    
    // Step 5: Show metrics
    console.log('\n5. Auth metrics:');
    authMonitor.logMetrics();
    
    console.log('\n✅ Auth loading fix complete!');
    console.log('If still stuck, try: window.clearAuthData() and sign in again');
    
  } catch (error) {
    console.error('❌ Fix failed:', error);
  } finally {
    console.groupEnd();
  }
}

/**
 * Nuclear option - clear everything and start fresh
 */
export async function resetAuthCompletely() {
  console.log('🔥 Resetting auth completely...');
  
  // Clear all auth data
  clearAuthData();
  
  // Force page reload
  window.location.href = '/';
}

// Export to window for console access
if (typeof window !== 'undefined') {
  (window as any).fixAuthLoading = fixAuthLoading;
  (window as any).resetAuthCompletely = resetAuthCompletely;
} 